// src/config/appBrands.js
//
// One place that ties each Halo app to its icon and its colour ramp, so anything
// showing "this belongs to Quda / MediDrop" reads the same everywhere. Screens
// carrying several apps at once (Check-Ins, admin overviews) can then be scanned
// by colour + icon instead of by reading labels.

import {
  QUDA_COLORS,
  MEDIDROP_COLORS,
  DOCLINK_COLORS,
  PHARMACY_HALO_COLORS,
} from "@/config/colors";

import QudaIcon from "@/Images/Icons/Quda_Icon_RGB.png";
import MediDropIcon from "@/Images/Icons/MediDrop_Icon_RGB.png";
import DocLinkIcon from "@/Images/Icons/DocLink_Icon_RGB.png";
import HaloTextIcon from "@/Images/Icons/HaloText_Icon_RGB.png";
import HaloPayIcon from "@/Images/Icons/HaloPay_Icon_RGB.png";
import ZephyrIcon from "@/Images/Icons/Zephyr_Icon_RGB.png";
import WebbitIcon from "@/Images/Icons/Webbit_Icon_RGB.png";
import HaloHireIcon from "@/Images/Icons/HaloHire_Icon_RGB.png";
import HaloExpireIcon from "@/Images/Icons/HaloExpire_Icon_RGB.png";

import QudaLogo from "@/Images/Logos/Quda_Logo.png";
import MediDropLogo from "@/Images/Logos/MediDrop_Logo.png";
import DocLinkLogo from "@/Images/Logos/DocLink_Logo.png";
import HaloTextLogo from "@/Images/Logos/HaloText_Logo.png";
import HaloPayLogo from "@/Images/Logos/HaloPay_Logo.png";
import ZephyrLogo from "@/Images/Logos/Zephyr_Logo.png";
import WebbitLogo from "@/Images/Logos/Webbit_Logo.png";
import HaloHireLogo from "@/Images/Logos/HaloHire_Logo.png";
import HaloExpireLogo from "@/Images/Logos/HaloExpire_Logo.png";
import PhLogoWhite from "@/Images/Logos/PHLogo_White.png";

/**
 * @typedef {Object} AppBrand
 * @property {string} name    Display name
 * @property {string} icon    Imported icon asset (square RGB app icon)
 * @property {string} logo    Imported wordmark logo (dark-on-transparent — light backgrounds only)
 * @property {string} main    Primary brand colour — chips, tab indicators, grid headers
 * @property {string} dark    Deeper shade — gradients, text on tinted backgrounds
 * @property {string} light   Mid/light shade — borders, ghost icons
 * @property {string} tint    Very light wash — hover rows, selected backgrounds
 */

export const APP_BRANDS = {
  quda: {
    name: "Quda",
    icon: QudaIcon,
    logo: QudaLogo,
    main: QUDA_COLORS.TEAL_DARK,
    dark: "#00615c",
    light: QUDA_COLORS.TEAL_MID,
    tint: QUDA_COLORS.TEAL_LIGHT,
  },
  medidrop: {
    name: "MediDrop",
    icon: MediDropIcon,
    logo: MediDropLogo,
    main: MEDIDROP_COLORS.ORANGE,
    dark: MEDIDROP_COLORS.ORANGE_DARK,
    light: MEDIDROP_COLORS.ORANGE_LIGHT,
    tint: MEDIDROP_COLORS.ORANGE_TINT,
  },
  doclink: {
    name: "DocLink",
    icon: DocLinkIcon,
    logo: DocLinkLogo,
    main: DOCLINK_COLORS.DARK_PURPLE,
    dark: "#2c1240",
    light: DOCLINK_COLORS.MID_PURPLE,
    tint: DOCLINK_COLORS.LAVENDER_TINT,
  },
  halotext: {
    name: "HaloText",
    icon: HaloTextIcon,
    logo: HaloTextLogo,
    main: "#0f766e",
    dark: "#115e59",
    light: "#5eead4",
    tint: "#f0fdfa",
  },
  halopay: {
    name: "HaloPay",
    icon: HaloPayIcon,
    logo: HaloPayLogo,
    main: "#1d4ed8",
    dark: "#1e3a8a",
    light: "#93c5fd",
    tint: "#eff6ff",
  },
  zephyr: {
    name: "Zephyr",
    icon: ZephyrIcon,
    logo: ZephyrLogo,
    main: "#0f172a",
    dark: "#020617",
    light: "#64748b",
    tint: "#f1f5f9",
  },
  webbit: {
    name: "Webbit",
    icon: WebbitIcon,
    logo: WebbitLogo,
    main: "#083b4b",
    dark: "#052730",
    light: "#94d628",
    tint: "#e8f7c5",
  },
  // HaloHire / HaloExpire share the suite navy + halo gold; their tints differ
  // (gold vs navy wash) so the two cards stay tellable-apart at a glance.
  halohire: {
    name: "HaloHire",
    icon: HaloHireIcon,
    logo: HaloHireLogo,
    main: PHARMACY_HALO_COLORS.BLUE,
    dark: "#001a54",
    light: PHARMACY_HALO_COLORS.YELLOW,
    tint: "#fff3d9",
  },
  haloexpire: {
    name: "HaloExpire",
    icon: HaloExpireIcon,
    logo: HaloExpireLogo,
    main: PHARMACY_HALO_COLORS.BLUE,
    dark: "#001a54",
    light: "#4b6bb0",
    tint: "#e7ecf8",
  },
  /** Group-level / not app-specific — falls back to the Pharmacy Halo palette. */
  halo: {
    name: "Pharmacy Halo",
    icon: null,
    logo: PhLogoWhite,
    main: PHARMACY_HALO_COLORS.BLUE,
    dark: "#001a54",
    light: "#4b6bb0",
    tint: "#eef2ff",
  },
};

export const getAppBrand = (key) => APP_BRANDS[key] || APP_BRANDS.halo;
